import { Filter, Query } from '@cubejs-client/core';

import { HIERARCHY_SEPARATOR } from '@/models/filter/utils/hierarchyPath';
import { Period } from '@/types/date';
import { SelectItem } from '@/types/ui/selectItem';
import { toStringOrNull } from '@/utils/cube/mapper';
import { CubeRawRow } from '@/utils/cube/mapper.type';

import { buildSubnationalSalesPeriodFilters, SUBNATIONAL_SALES_CUBE_NAME } from './subnationalSalesOptionsQuery';

export const BRICK_CODE = `${SUBNATIONAL_SALES_CUBE_NAME}.geography_brick_code`;
// Level 1 is the row's own area (a Sub-brick, or a Brick); level 2 is its parent (the Brick, or the Department).
export const GEOGRAPHY_TYPE = `${SUBNATIONAL_SALES_CUBE_NAME}.geography_level_1_type`;
export const GEOGRAPHY_CODE = `${SUBNATIONAL_SALES_CUBE_NAME}.geography_level_1_type_code`;
export const GEOGRAPHY_NAME = `${SUBNATIONAL_SALES_CUBE_NAME}.geography_level_1_name`;
export const PARENT_GEOGRAPHY_NAME = `${SUBNATIONAL_SALES_CUBE_NAME}.geography_level_2_name`;

export const BRICK_TYPE = 'Brick';
export const SUB_BRICK_TYPE = 'Sub-brick';

const GEOGRAPHY_DIMENSIONS = [BRICK_CODE, GEOGRAPHY_TYPE, GEOGRAPHY_CODE, GEOGRAPHY_NAME, PARENT_GEOGRAPHY_NAME];

// The tree nests options in plain objects, which move integer-like keys first: the prefix keeps the name order.
const CODE_PREFIX = '#';

export interface BrickPath {
  brickCode: string;
  subBrickCode?: string;
}

export interface SalesBrickOptions {
  options: SelectItem<string>[];
  optionLabels: ReadonlyMap<string, string>;
}

interface SalesBrickOptionsQueryParams {
  periods: readonly (Period | undefined)[];
}

export const toBrickPath = (brickCode: string, subBrickCode?: string): string =>
  subBrickCode
    ? `${CODE_PREFIX}${brickCode}${HIERARCHY_SEPARATOR}${CODE_PREFIX}${subBrickCode}`
    : `${CODE_PREFIX}${brickCode}`;

const toCode = (segment: string | undefined): string | null =>
  segment?.startsWith(CODE_PREFIX) ? segment.slice(CODE_PREFIX.length) : null;

export const parseBrickPath = (path: string): BrickPath | null => {
  const [brickSegment, subBrickSegment, ...extraSegments] = path.split(HIERARCHY_SEPARATOR);
  const brickCode = toCode(brickSegment);
  if (!brickCode || extraSegments.length) return null;
  if (subBrickSegment === undefined) return { brickCode };
  const subBrickCode = toCode(subBrickSegment);
  return subBrickCode ? { brickCode, subBrickCode } : null;
};

/** Lists the bricks and sub-bricks sold in the periods. */
export const buildSalesBrickOptionsQuery = ({ periods }: SalesBrickOptionsQueryParams): Query => ({
  dimensions: [...GEOGRAPHY_DIMENSIONS],
  filters: buildSubnationalSalesPeriodFilters(periods),
  // Keeps each brick's rows together, so the row limit cuts between bricks.
  order: { [BRICK_CODE]: 'asc', [GEOGRAPHY_CODE]: 'asc' },
});

/** Finds the names of selected bricks and sub-bricks in any period, so a selection never shows its codes. */
export const buildSalesBrickLabelsQuery = (paths: readonly string[]): Query | undefined => {
  const brickCodes = new Set<string>();
  const subBrickCodes = new Set<string>();
  paths.forEach(path => {
    const brickPath = parseBrickPath(path);
    if (brickPath?.subBrickCode) subBrickCodes.add(brickPath.subBrickCode);
    else if (brickPath) brickCodes.add(brickPath.brickCode);
  });

  const codeFilters: Filter[] = [];
  if (brickCodes.size) codeFilters.push({ member: BRICK_CODE, operator: 'equals', values: [...brickCodes] });
  if (subBrickCodes.size) codeFilters.push({ member: GEOGRAPHY_CODE, operator: 'equals', values: [...subBrickCodes] });
  if (!codeFilters.length) return undefined;

  return {
    dimensions: [...GEOGRAPHY_DIMENSIONS],
    filters: codeFilters.length > 1 ? [{ or: codeFilters }] : codeFilters,
  };
};

/** Turns geography rows into brick options with their sub-bricks nested below, labelled by name. */
export const buildSalesBrickOptions = (rows: CubeRawRow[]): SalesBrickOptions => {
  const options: SelectItem<string>[] = [];
  const optionLabels = new Map<string, string>();
  const addOption = (path: string, name: string) => {
    if (optionLabels.has(path)) return;
    optionLabels.set(path, name);
    options.push({ value: path, label: name });
  };

  rows.forEach(geographyRow => {
    const brickCode = toStringOrNull(geographyRow[BRICK_CODE]);
    const geographyType = toStringOrNull(geographyRow[GEOGRAPHY_TYPE]);
    if (!brickCode) return;

    if (geographyType === BRICK_TYPE) {
      addOption(toBrickPath(brickCode), toStringOrNull(geographyRow[GEOGRAPHY_NAME], brickCode));
      return;
    }
    if (geographyType !== SUB_BRICK_TYPE) return;

    addOption(toBrickPath(brickCode), toStringOrNull(geographyRow[PARENT_GEOGRAPHY_NAME], brickCode));
    const subBrickCode = toStringOrNull(geographyRow[GEOGRAPHY_CODE]);
    if (subBrickCode) {
      addOption(toBrickPath(brickCode, subBrickCode), toStringOrNull(geographyRow[GEOGRAPHY_NAME], subBrickCode));
    }
  });

  return { options, optionLabels };
};
